import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { FaqAccordion } from "@/components/FaqAccordion";
import { FAQS, PAGES } from "@/lib/site";

const CRUMBS = [{ name: "Home", path: "/" }, { name: "FAQ", path: "/faq" }];
const CATS = ["All", ...Array.from(new Set(FAQS.map((f) => f.cat)))];

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

export default function Faq() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const items = useMemo(() => {
    const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return FAQS.filter((f) => {
      const text = `${f.q} ${f.a} ${f.cat}`.toLowerCase();
      return (cat === "All" || f.cat === cat) && terms.every((t) => text.includes(t));
    });
  }, [q, cat]);

  return (
    <>
      <Seo {...PAGES.faq} path="/faq" crumbs={CRUMBS} jsonLd={FAQ_LD} />
      <PageHeader crumbs={CRUMBS} eyebrow="Good to know" title="Frequently asked" highlight="questions" image="/img/stairs-2.webp" lead="Medical cards, ordering, hours and finding the door — straight answers from District Gaslight." />
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20" data-testid="faq-page">
        <div className="grid gap-12 lg:grid-cols-12">
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-36">
              <label htmlFor="faq-search" className="eyebrow">Search questions</label>
              <div className="mt-3 flex items-center gap-3 rounded-full border border-neutral-300 bg-white px-5 focus-within:border-brand-ink">
                <Search className="h-4 w-4 text-neutral-500" aria-hidden="true" />
                <input id="faq-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. medical card, hours" className="h-12 w-full bg-transparent placeholder:text-neutral-400 focus:outline-none" data-testid="faq-search-input" />
              </div>
              <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Filter by topic">
                {CATS.map((c) => (
                  <button key={c} type="button" onClick={() => setCat(c)} aria-pressed={cat === c} data-testid={`faq-filter-${c.toLowerCase()}`} className={`chip ${cat === c ? "chip-active" : ""}`}>{c}</button>
                ))}
              </div>
            </div>
          </aside>
          <div className="lg:col-span-8">
            <p className="mb-2 text-xs font-semibold text-neutral-500" aria-live="polite" data-testid="faq-result-count">{items.length} {items.length === 1 ? "answer" : "answers"}</p>
            {items.length ? (
              <FaqAccordion items={items} />
            ) : (
              <div className="rounded-2xl bg-brand-sand p-10 text-center" data-testid="faq-empty-state">
                <p className="font-display text-2xl font-bold">No answers match "{q}".</p>
                <p className="mt-3 text-sm text-neutral-600">Try another word, or <Link to="/location" className="font-bold text-brand-red">contact us</Link> directly.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
